import { h, Host } from "@stencil/core";
import { clamp, isNumber, range } from "../../utils/utils";
import { ButtonSize, ButtonVariant } from "../button/button.enum";
const baseClass = "ux-pagination";
export class Pagination {
    constructor() {
        this.totalPages = undefined;
        this.page = 1;
        this.inputDebounceMs = 750;
        this.maxVisible = 7;
        this.boundaryCount = undefined;
        this.siblingCount = undefined;
        this.width = undefined;
    }
    // Space required for ellipsis and active page
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static RESERVED_SPACE = 3;
    observer;
    desiredBoundaryCount;
    desiredSiblingCount;
    buttonWidth = 60; // largest current button size, only as default
    host;
    validateRequiredProps(value) {
        const isBlank = !isNumber(value) || value <= 0;
        if (isBlank) {
            throw new Error("total-pages is required and has to be greater than 0");
        }
    }
    updateDesiredCounts() {
        const fourthOfAvailableSpace = Math.floor((this.maxVisible - Pagination.RESERVED_SPACE) / 4);
        this.desiredBoundaryCount = this.boundaryCount !== undefined ? this.boundaryCount : fourthOfAvailableSpace;
        this.desiredSiblingCount = this.siblingCount !== undefined ? this.siblingCount : fourthOfAvailableSpace;
    }
    /** Emitted on page changes */
    change;
    debouncedHandleInputChanges;
    connectedCallback() {
        if (!window.ResizeObserver) {
            return;
        }
        this.observer = new ResizeObserver((entries) => {
            this.updateButtonWidth();
            const entry = entries[0];
            if (entry.contentBoxSize) {
                const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
                this.width = contentBoxSize.inlineSize;
            }
            else {
                this.width = entry.contentRect.width;
            }
        });
    }
    componentWillLoad() {
        this.updateDesiredCounts();
        if (!window.ResizeObserver) {
            setTimeout(() => {
                this.updateButtonWidth();
                this.width = this.host.getBoundingClientRect().width;
            });
        }
        else {
            this.observer.observe(this.host);
        }
        let timer;
        this.debouncedHandleInputChanges = (event) => {
            clearTimeout(timer);
            timer = setTimeout(() => this.handleInputChanges(event), this.inputDebounceMs);
        };
    }
    updateButtonWidth() {
        const buttonWidth = parseInt(getComputedStyle(this.host).getPropertyValue("--ux-pagination-button-width").replace("px", ""), 10);
        if (isNumber(buttonWidth) && buttonWidth > 0) {
            this.buttonWidth = buttonWidth;
        }
    }
    handleKeydown(event) {
        event.stopPropagation();
        if (event.key === "Enter") {
            // prevent click event on ellipsis button focused after closing
            event.preventDefault();
            const numberInput = event.target.closest("ux-input-field-number");
            // @ts-expect-error Check for typing
            this.handleInput(numberInput);
        }
    }
    handleInputChanges(event) {
        if (!event.target) {
            return;
        }
        const target = event.target;
        this.handleInput(target);
    }
    handlePopoverHidden(event) {
        if (!event.target) {
            return;
        }
        const numberInput = event.target.querySelector("ux-input-field-number");
        // @ts-expect-error Check for typing
        this.handleInput(numberInput);
    }
    handleInput(input) {
        if (!input) {
            return;
        }
        if (isNaN(input.value)) {
            return;
        }
        this.moveToPage(input.value);
    }
    moveToPage(p) {
        const prev = this.page;
        this.page = clamp(p, 1, this.totalPages);
        if (prev !== this.page) {
            this.change.emit({ page: this.page });
        }
    }
    renderMobilePageInput() {
        return (h("ux-input-field-number", { defaultValue: 1, min: 1, max: this.totalPages, unit: this.totalPages.toString(), value: this.page, onUxChange: (e) => this.debouncedHandleInputChanges(e), onKeyDown: (e) => this.handleKeydown(e) }));
    }
    renderPageButtons(count, startAt = 1) {
        return (h("div", { class: `${baseClass}__button-group` }, range(count, startAt).map((p) => (h("ux-button", { class: `${baseClass}__page-button`, selected: p === this.page, onClick: () => this.moveToPage(p) }, p)))));
    }
    handlePopoverShow = (event) => {
        const input = event.target.querySelector("ux-input-field-number");
        if (input) {
            setTimeout(() => input.setFocus(), 100);
        }
    };
    renderEllipsis(position) {
        const placement = position === "left" ? "top-start" : "top-end";
        return (h("ux-popover", { placement: placement, onUxHide: (e) => this.handlePopoverHidden(e), onUxShow: (e) => this.handlePopoverShow(e) }, h("ux-button", { class: `${baseClass}__ellipsis`, icon: "ellipsis", slot: "trigger", variant: ButtonVariant.NoBorder }), h("ux-popover-panel", null, this.renderMobilePageInput())));
    }
    renderPartialPages(availableSpace) {
        const remainingSpace = availableSpace - Pagination.RESERVED_SPACE;
        const desiredTotal = Math.max(1, this.desiredBoundaryCount + this.desiredSiblingCount);
        const boundaryFraction = this.desiredBoundaryCount / desiredTotal;
        const boundaryCount = Math.min(this.desiredBoundaryCount, Math.ceil((remainingSpace * boundaryFraction) / 2));
        const siblingCount = Math.min(this.desiredSiblingCount, Math.floor((remainingSpace - boundaryCount * 2) / 2));
        const boundaryLimit = boundaryCount + siblingCount * 2 + 2;
        if (this.page <= boundaryLimit - siblingCount) {
            return [
                this.renderPageButtons(boundaryLimit, 1),
                this.renderEllipsis("right"),
                this.renderPageButtons(boundaryCount, this.totalPages - boundaryCount + 1)
            ];
        }
        if (this.page > this.totalPages - (boundaryLimit - siblingCount)) {
            return [
                this.renderPageButtons(boundaryCount, 1),
                this.renderEllipsis("left"),
                this.renderPageButtons(boundaryLimit, this.totalPages - boundaryLimit + 1)
            ];
        }
        return [
            this.renderPageButtons(boundaryCount, 1),
            this.renderEllipsis("left"),
            this.renderPageButtons(siblingCount * 2 + 1, this.page - siblingCount),
            this.renderEllipsis("right"),
            this.renderPageButtons(boundaryCount, this.totalPages - boundaryCount + 1)
        ];
    }
    renderPages() {
        const calculatedSpace = this.desiredBoundaryCount * 2 + this.desiredSiblingCount * 2 + Pagination.RESERVED_SPACE;
        const availableSpace = Math.min(Math.floor(this.width / this.buttonWidth) - 2, Math.max(this.maxVisible, calculatedSpace));
        if (availableSpace < Pagination.RESERVED_SPACE + 2) {
            return this.renderMobilePageInput();
        }
        if (this.totalPages <= availableSpace) {
            return this.renderPageButtons(this.totalPages);
        }
        return this.renderPartialPages(availableSpace);
    }
    render() {
        if (!this.width) {
            return;
        }
        return (h(Host, null, h("ux-button", { disabled: this.page === 1, size: ButtonSize.Icon, icon: "chevron-left", onClick: () => this.moveToPage(this.page - 1) }), h("div", { class: `${baseClass}__inner` }, this.renderPages()), h("ux-button", { disabled: this.page === this.totalPages, size: ButtonSize.Icon, icon: "chevron-right", onClick: () => this.moveToPage(this.page + 1) })));
    }
    static get is() { return "ux-pagination"; }
    static get originalStyleUrls() {
        return {
            "$": ["pagination.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["pagination.css"]
        };
    }
    static get properties() {
        return {
            "totalPages": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The total page count"
                },
                "attribute": "total-pages",
                "reflect": false
            },
            "page": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current page, in the range of 1 - total-pages"
                },
                "attribute": "page",
                "reflect": true,
                "defaultValue": "1"
            },
            "inputDebounceMs": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The waiting time after input changes (mobile layout and ellipsis inputs)\nbefore the page is updated"
                },
                "attribute": "input-debounce-ms",
                "reflect": false,
                "defaultValue": "750"
            },
            "maxVisible": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The maximum visible page count, useful to force narrow layout"
                },
                "attribute": "max-visible",
                "reflect": false,
                "defaultValue": "7"
            },
            "boundaryCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number of always visible pages at the beginning and end.\nIf not set, will be calculated based on `maxVisible` ((maxVisible - 3) / 4).\nMay overwrite max-visible."
                },
                "attribute": "boundary-count",
                "reflect": false
            },
            "siblingCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Number of always visible pages before and after the current one.\nIf not set, will be calculated based on `maxVisible` ((maxVisible - 3) / 4).\nMay overwrite max-visible."
                },
                "attribute": "sibling-count",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "width": {}
        };
    }
    static get events() {
        return [{
                "method": "change",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted on page changes"
                },
                "complexType": {
                    "original": "{ page: number }",
                    "resolved": "{ page: number; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "totalPages",
                "methodName": "validateRequiredProps"
            }, {
                "propName": "maxVisible",
                "methodName": "updateDesiredCounts"
            }, {
                "propName": "boundaryCount",
                "methodName": "updateDesiredCounts"
            }, {
                "propName": "siblingCount",
                "methodName": "updateDesiredCounts"
            }];
    }
}
//# sourceMappingURL=pagination.js.map
